import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  InputLabel,
  FormControl,
  Paper,
  Stack,
  Divider,
  Chip,
  InputAdornment
} from "@mui/material";

export default function InvoiceProductForm({ producto, onAdd, bodegaId = "" }) {
  const [pvp, setPvp] = useState("");
  const [brand, setBrand] = useState("");
  const [ivaPorcentaje, setIvaPorcentaje] = useState(15);
  const [ivaValor, setIvaValor] = useState("0.000000");
  const [descuentoPct, setDescuentoPct] = useState("0");
  const [stock, setStock] = useState(0);
  const [detalles, setDetalles] = useState("");
  const [codigoPrincipal, setCodigoPrincipal] = useState("");
  const [codigoAuxiliar, setCodigoAuxiliar] = useState("");
  const [sku, setSku] = useState("");

  const API_TOKEN = process.env.REACT_APP_API_TOKEN || "";

  const normalizeDecimal = (v) => {
    let s = String(v ?? "");
    s = s.replace(/,/g, ".");
    s = s.replace(/[^\d.]/g, "");
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
      const parts = s.split(".");
      s = `${parts[0]}.${(parts[1] || "").slice(0, 6)}`;
    }
    return s;
  };

  const roundTo6 = (value) => {
    const num = Number(value) || 0;
    return (Math.round((num + Number.EPSILON) * 1000000) / 1000000).toFixed(6);
  };

  const getItemLabel = (item) => {
    if (!item) return "";

    if (item.type === "product" && item.variant_id) {
      const baseName = (item.name || "").split(" (")[0];
      return `${item.code || ""} ${baseName} (${item.sku || ""} ${item.value || ""})`.trim();
    }

    return `${item.code || ""} ${item.name || ""}`.trim();
  };

  const findSameItem = (results, current) => {
    const productId = String(current?.product_id ?? current?.id ?? "").trim();
    const variantId = current?.variant_id !== null && current?.variant_id !== undefined
      ? String(current.variant_id).trim()
      : "";
    const code = String(current?.code || "").trim();
    const currentSku = String(current?.sku || "").trim();
    const currentCodigoPrincipal = String(current?.codigo_principal || "").trim();

    const byId = results.find((item) => {
      const itemProductId = String(item.product_id ?? item.id ?? "").trim();
      const itemVariantId = item.variant_id !== null && item.variant_id !== undefined
        ? String(item.variant_id).trim()
        : "";

      if (!productId || !itemProductId) return false;

      if (variantId) {
        return itemProductId === productId && itemVariantId === variantId;
      }

      return itemProductId === productId && !itemVariantId;
    });

    if (byId) return byId;

    return results.find((item) => {
      const itemCode = String(item.code || "").trim();
      const itemSku = String(item.sku || "").trim();
      const itemCodigoPrincipal = String(item.codigo_principal || "").trim();
      const itemVariantId = item.variant_id !== null && item.variant_id !== undefined
        ? String(item.variant_id).trim()
        : "";

      if (variantId) {
        return (
          (itemVariantId && itemVariantId === variantId) ||
          (currentCodigoPrincipal && itemCodigoPrincipal === currentCodigoPrincipal) ||
          (code && itemCode === code && currentSku && itemSku === currentSku)
        );
      }

      return (
        (currentCodigoPrincipal && itemCodigoPrincipal === currentCodigoPrincipal) ||
        (code && itemCode === code && !itemVariantId)
      );
    }) || null;
  };

  useEffect(() => {
    if (producto) {
      const precio = Number(producto.pvp1 || producto.pvp || 0);
      const code = producto.code || "";
      const productSku = producto.sku || "";
      const principal = producto.codigo_principal || (code && productSku ? `${code}-${productSku}` : code);
      const porc = Number(producto.iva_porcentaje ?? 15);

      setPvp(roundTo6(precio));
      setBrand(producto.brand || "");
      setIvaPorcentaje(porc);
      setIvaValor(porc === 999 ? "0.000000" : roundTo6((precio * porc) / 100));
      setDescuentoPct(String(Number(producto.descuentoPct || 0)));
      setStock(Number(producto.stock ?? 0));
      setDetalles("");
      setCodigoPrincipal(principal);
      setCodigoAuxiliar(producto.codigo_auxiliar || "");
      setSku(productSku);
    } else {
      setPvp("");
      setBrand("");
      setIvaPorcentaje(15);
      setIvaValor("0.000000");
      setDescuentoPct("0");
      setStock(0);
      setDetalles("");
      setCodigoPrincipal("");
      setCodigoAuxiliar("");
      setSku("");
    }
  }, [producto]);

  useEffect(() => {
    let active = true;

    const refreshStockByBodega = async () => {
      if (!producto || producto.type === "service" || !bodegaId) return;

      const q =
        producto.codigo_principal ||
        producto.code ||
        producto.barcode ||
        producto.sku ||
        producto.name ||
        "";

      if (!q) return;

      try {
        const params = new URLSearchParams();
        params.set("q", q);
        params.set("bodega_id", bodegaId);

        const resp = await fetch(`/proformas/buscar-items/?${params.toString()}`, {
          headers: { "X-API-TOKEN": API_TOKEN },
          credentials: "include"
        });

        const data = await resp.json().catch(() => ({}));
        const results = Array.isArray(data.results) ? data.results : [];
        const item = findSameItem(results, producto);

        if (active && item) {
          setStock(Number(item.stock ?? 0));
        }

        if (active && !item) {
          setStock(0);
        }
      } catch {}
    };

    refreshStockByBodega();

    return () => {
      active = false;
    };
  }, [producto, bodegaId, API_TOKEN]);

  useEffect(() => {
    const precio = Number(normalizeDecimal(pvp)) || 0;
    const porc = Number(ivaPorcentaje) || 0;

    setIvaValor(
      porc === 999
        ? "0.000000"
        : roundTo6((precio * porc) / 100)
    );
  }, [pvp, ivaPorcentaje]);

  const precioFinal = useMemo(() => {
    const precio = Number(normalizeDecimal(pvp)) || 0;
    const descuento = Number(normalizeDecimal(descuentoPct)) || 0;
    const precioConDescuento = Math.max(precio - (precio * descuento) / 100, 0);
    const porc = Number(ivaPorcentaje) || 0;
    const iva = porc === 999 ? 0 : (precioConDescuento * porc) / 100;
    return roundTo6(precioConDescuento + iva);
  }, [pvp, descuentoPct, ivaPorcentaje]);

  const handleAdd = () => {
    if (!producto) return;

    const precio = Number(normalizeDecimal(pvp));
    if (Number.isNaN(precio)) return;

    onAdd({
      product_id: producto.product_id ?? producto.id ?? null,
      variant_id: producto.variant_id ?? null,
      code: producto.code || "",
      sku: sku || "",
      codigo_principal: codigoPrincipal || producto.codigo_principal || producto.code || "",
      codigo_auxiliar: codigoAuxiliar || "",
      name: producto.name || "",
      variant_type: producto.variant_type || "",
      value: producto.value || "",
      pvp: roundTo6(precio),
      iva_porcentaje: Number(ivaPorcentaje),
      iva_valor: ivaPorcentaje === 999 ? "0.000000" : roundTo6((precio * Number(ivaPorcentaje || 0)) / 100),
      descuentoPct: String(Number(normalizeDecimal(descuentoPct) || 0)),
      stock: stock,
      detalles: detalles.trim(),
      is_service: producto.type === "service" || producto.code?.startsWith("S"),
      brand: brand,
      type: producto.type || "",
      bodega_id: bodegaId || ""
    });

    setPvp("");
    setBrand("");
    setIvaPorcentaje(15);
    setIvaValor("0.000000");
    setDescuentoPct("0");
    setStock(0);
    setDetalles("");
    setCodigoPrincipal("");
    setCodigoAuxiliar("");
    setSku("");
  };

  return (
    <Grid item xs={12}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)"
        }}
      >
        <Grid container spacing={2.2} alignItems="stretch">
          <Grid item xs={12}>
            <Stack spacing={2.2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Marca"
                    value={brand}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3,
                        backgroundColor: "#f7f9fc"
                      },
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Código principal"
                    value={codigoPrincipal}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3,
                        backgroundColor: "#f7f9fc"
                      },
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Código auxiliar"
                    value={codigoAuxiliar}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3,
                        backgroundColor: "#f7f9fc"
                      },
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="SKU"
                    value={sku}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3,
                        backgroundColor: "#f7f9fc"
                      },
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Precio Unitario"
                    type="text"
                    inputProps={{ inputMode: "decimal" }}
                    value={pvp}
                    onChange={(e) => setPvp(normalizeDecimal(e.target.value))}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      sx: { borderRadius: 3 }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>IVA (%)</InputLabel>
                    <Select
                      value={ivaPorcentaje}
                      label="IVA (%)"
                      onChange={(e) => setIvaPorcentaje(Number(e.target.value))}
                      sx={{ borderRadius: 3 }}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={15}>15%</MenuItem>
                      <MenuItem value={999}>No objeto</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="IVA ($)"
                    value={ivaValor}
                    disabled
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      sx: { borderRadius: 3, backgroundColor: "#f7f9fc" }
                    }}
                    sx={{
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Descuento (%)"
                    type="text"
                    inputProps={{ inputMode: "decimal" }}
                    value={descuentoPct}
                    onChange={(e) => setDescuentoPct(normalizeDecimal(e.target.value))}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      sx: { borderRadius: 3 }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Stock"
                    value={stock}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3,
                        backgroundColor: "#f7f9fc"
                      },
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#1f2937 !important"
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Precio final"
                    value={precioFinal}
                    disabled
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      sx: { borderRadius: 3, backgroundColor: "#eef7ff" }
                    }}
                    sx={{
                      "& .Mui-disabled": {
                        WebkitTextFillColor: "#0f172a !important",
                        fontWeight: 700
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Detalles adicionales"
                    value={detalles}
                    onChange={(e) => setDetalles(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: 3
                      }
                    }}
                  />
                </Grid>
              </Grid>

              <Divider />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={producto?.type === "service" ? "Servicio" : "Producto"}
                    color={producto?.type === "service" ? "secondary" : "primary"}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  />
                  {producto?.variant_id ? (
                    <Chip
                      label={`${producto.variant_type || "Variante"}: ${producto.value || ""}`}
                      color="info"
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    />
                  ) : null}
                </Stack>

                <Button
                  variant="contained"
                  onClick={handleAdd}
                  fullWidth={false}
                  sx={{
                    minWidth: { xs: "100%", sm: 220 },
                    height: 48,
                    borderRadius: 3,
                    textTransform: "none",
                    bgcolor: "#1565C0",
                    "&:hover": { bgcolor: "#0D47A1" }
                  }}
                >
                  Añadir producto
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}